// FTEDN Creator — Beta Lab Page (wired to real data)
import { useEffect, useState } from 'react';
import { useBetaStore } from '../../stores/beta.store';
import { useUserStore } from '../../stores/user.store';
import { ProgramCard } from './ProgramCard';
import { HowItWorks } from './HowItWorks';
import { checkEligibility, calculateTesterScore } from './beta.helpers';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import './BetaProgramPage.css';

export function BetaProgramPage() {
  const [showHow, setShowHow] = useState(false);
  const { programs, applications, loading, fetchPrograms, fetchMyApplications, applyToProgram } = useBetaStore();
  const { profile } = useUserStore();
  const { addToast } = useToast();

  useEffect(() => {
    fetchPrograms();
    fetchMyApplications();
  }, [fetchPrograms, fetchMyApplications]);

  const testerScore = profile
    ? calculateTesterScore({
        reviewCount: profile.reviewCount,
        streak: profile.streak,
      })
    : 0;

  const handleApply = async (programId: string) => {
    const ok = await applyToProgram(programId);
    if (ok) {
      addToast({ type: 'success', title: 'Applied!', message: 'You\'ll hear back within 48 hours.' });
      fetchMyApplications();
    } else {
      addToast({ type: 'error', title: 'Failed', message: 'Could not submit application.' });
    }
  };

  return (
    <div className="beta-page page-enter">
      <div className="beta-header">
        <div className="beta-header-left">
          <h1>🧪 Beta Lab</h1>
          <p className="beta-subtitle">Test products before anyone else. Your feedback shapes the formula.</p>
        </div>
        <div className="beta-header-right">
          <div className="tester-score">
            <div className="tester-score-ring">
              <svg viewBox="0 0 36 36" className="tester-svg">
                <path className="tester-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path className="tester-fill" strokeDasharray={`${testerScore}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              </svg>
              <span className="tester-score-num">{testerScore}</span>
            </div>
            <span className="tester-label">Tester Score</span>
          </div>
        </div>
      </div>

      <div className="beta-phases-legend">
        <div className="phase-item phase-apply">📋 Apply</div>
        <div className="phase-connector" />
        <div className="phase-item phase-ship">📦 Shipped</div>
        <div className="phase-connector" />
        <div className="phase-item phase-test">🧪 Testing</div>
        <div className="phase-connector" />
        <div className="phase-item phase-done">✅ Complete</div>
      </div>

      <button className="neo-btn neo-btn--ghost beta-how-btn" onClick={() => setShowHow(!showHow)}>
        {showHow ? 'Hide Details' : '❓ How It Works'}
      </button>
      {showHow && <HowItWorks />}

      {loading ? (
        <div className="programs-grid">
          <Skeleton variant="card" count={3} />
        </div>
      ) : programs.length === 0 ? (
        <EmptyState
          icon="🧪"
          title="No active programs"
          description="Check back soon — new beta programs are launched regularly."
        />
      ) : (
        <div className="programs-grid">
          {programs.map((p, i) => {
            const eligibility = checkEligibility(p, applications, profile?.level ?? 1);
            return (
              <ProgramCard
                key={p.id}
                program={{ ...p, desc: p.description, spots: p.spotsLeft }}
                index={i}
                eligible={eligibility.eligible}
                reason={!eligibility.eligible ? eligibility.reason : undefined}
                onApply={() => handleApply(p.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
